// ===== ANALIZADOR DE HISTORIAL V3.0 (ARQUITECTURA ROBUSTA) =====
// ZERO-PATCH POLICY: Lógica centralizada y delegación de eventos global.

import { analyzeBatchWithGemini, generateCaseSummaryWithGemini, geminiAnalysisToTimelineEvent } from './geminiAnalyzer.js';
import { CaseService } from './caseService.js';
import { AuthService } from './authService.js';
import { analyzeDocumentWithTriage, processBatchWithTriage, getTriageIcon, getActionLabel, generateTriageReport } from './documentTriage.js';

// =========================================================================
// 1. UTILIDADES
// =========================================================================

const safeDate = (val) => {
  if (!val) return new Date();
  if (val?.toDate) return val.toDate(); // Firestore Timestamp
  const d = new Date(val);
  return isNaN(d.getTime()) ? new Date() : d;
};

// =========================================================================
// 2. SISTEMA DE EVENTOS GLOBAL (SINGLE SOURCE OF TRUTH)
// =========================================================================

/**
 * Inicializa el sistema de escucha global para el módulo de historial.
 * Se debe llamar UNA VEZ en main.js al arrancar la app.
 */
export function initHistorialSystem() {
  console.log('🔧 Inicializando Sistema de Historial v3.0...');

  // Limpiar listener previo si existiera (aunque en carga inicial no debería)
  document.removeEventListener('click', handleGlobalClick);

  // Asignar listener global al documento
  document.addEventListener('click', handleGlobalClick);

  console.log('✅ Sistema de Historial v3.0 ESCUCHANDO (Modo Delegación Global).');
}

/**
 * Manejador central de clics para todo el módulo.
 * Intercepta clics en elementos con data-action="edit-document"
 */
function handleGlobalClick(e) {
  // Buscar si el clic fue dentro de un botón de editar
  const editBtn = e.target.closest('[data-action="edit-document"]');

  if (editBtn) {
    e.preventDefault(); // Prevenir comportamientos default
    e.stopPropagation(); // Detener propagación

    const actionId = editBtn.dataset.id;
    const caseId = editBtn.dataset.caseId;

    if (actionId && caseId) {
      console.log(`🖱️ CLICK CAPTURADO: Editar documento ${actionId} del caso ${caseId}`);
      openEditModal(actionId, caseId);
    } else {
      console.warn('⚠️ Botón de editar sin IDs válidos:', editBtn);
    }
  }

  // Otros manejadores globales pueden ir aquí (ej: ver documento, borrar)
}


// =========================================================================
// 3. LÓGICA DE NEGOCIO (MODAL Y GUARDADO)
// =========================================================================

async function openEditModal(actionId, caseId) {
  try {
    const actions = await CaseService.getActions(caseId);
    const action = actions.find(a => a.id === actionId);

    if (!action) {
      alert('❌ Error: Documento no encontrado en la base de datos.');
      return;
    }

    renderEditModal(action, caseId);

  } catch (error) {
    console.error('Error abriendo modal:', error);
    alert('Error del sistema: ' + error.message);
  }
}

function renderEditModal(action, caseId) {
  // Eliminar modal previo si existe
  const existingModal = document.getElementById('historial-edit-modal');
  if (existingModal) existingModal.remove();

  const modal = document.createElement('div');
  modal.id = 'historial-edit-modal';
  modal.className = 'modal fade-in';
  modal.style.display = 'flex';

  // Datos pre-llenados
  const currentType = Array.isArray(action.category) ? action.category[0] : (action.category || '');
  const dateObj = safeDate(action.createdTime);
  const dateStr = dateObj.toISOString().split('T')[0];
  const notes = action.description || '';
  const name = action.name || '';

  modal.innerHTML = `
    <div class="glass-container" style="width: 90%; max-width: 500px; padding: 2rem;">
      <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 1rem;">
        <h3 style="margin: 0; color: var(--primary-gold);">✏️ Editar Documento</h3>
        <button type="button" id="btn-ai-autocomplete" class="btn-outline" style="font-size: 0.8rem; padding: 0.3rem 0.6rem; border-color: var(--primary-gold); color: var(--primary-gold);">
          ✨ Autocompletar con IA
        </button>
      </div>
      
      <form id="edit-doc-form">
        <div class="form-group">
          <label>Nombre del Documento</label>
          <input type="text" name="nombre" id="input-nombre" value="${name}" required style="width: 100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border);">
        </div>

        <div class="form-group">
          <label>Tipo de Clasificación</label>
          <select name="tipo" id="input-tipo" required style="width: 100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border);">
             <option value="">Seleccionar...</option>
             <option value="resolucion" ${currentType.toLowerCase().includes('resolucion') ? 'selected' : ''}>Resolución</option>
             <option value="sentencia" ${currentType.toLowerCase().includes('sentencia') ? 'selected' : ''}>Sentencia</option>
             <option value="demanda" ${currentType.toLowerCase().includes('demanda') ? 'selected' : ''}>Demanda</option>
             <option value="escrito" ${currentType.toLowerCase().includes('escrito') ? 'selected' : ''}>Escrito</option>
             <option value="notificacion" ${currentType.toLowerCase().includes('notificacion') ? 'selected' : ''}>Notificación</option>
             <option value="prueba" ${currentType.toLowerCase().includes('prueba') ? 'selected' : ''}>Prueba</option>
             <option value="contrato" ${currentType.toLowerCase().includes('contrato') ? 'selected' : ''}>Contrato</option>
             <option value="borrador" ${currentType.toLowerCase().includes('borrador') ? 'selected' : ''}>Borrador</option>
             <option value="otro" ${currentType.toLowerCase().includes('otro') ? 'selected' : ''}>Otro</option>
          </select>
        </div>
        
        <div class="form-group">
          <label>Fecha del Documento</label>
          <input type="date" name="fecha" id="input-fecha" value="${dateStr}" required style="width: 100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border);">
        </div>
        
        <div class="form-group">
          <label>Notas / Resumen</label>
          <textarea name="notas" id="input-notas" rows="3" style="width: 100%; padding: 0.8rem; border-radius: 8px; background: rgba(0,0,0,0.3); color: white; border: 1px solid var(--glass-border);">${notes}</textarea>
        </div>
        
        <div style="display: flex; gap: 10px; margin-top: 2rem;">
          <button type="button" class="btn-secondary" style="flex: 1;" onclick="document.getElementById('historial-edit-modal').remove()">Cancelar</button>
          <button type="submit" class="btn-primary" style="flex: 1;">Guardar Cambios</button>
        </div>
      </form>
    </div>
  `;

  document.body.appendChild(modal);

  // Bind Form Submit
  const form = modal.querySelector('#edit-doc-form');
  form.addEventListener('submit', (e) => handleEditSubmit(e, action, caseId));

  // Bind AI Button
  const aiBtn = modal.querySelector('#btn-ai-autocomplete');
  aiBtn.addEventListener('click', () => handleAIAutocomplete(aiBtn, action, modal));
}

async function handleEditSubmit(e, action, caseId) {
  e.preventDefault();
  const form = e.target;
  const btn = form.querySelector('button[type="submit"]');

  const originalText = btn.innerText;
  btn.innerText = 'Guardando...';
  btn.disabled = true;

  try {
    const formData = new FormData(form);
    const nombre = formData.get('nombre');
    const tipo = formData.get('tipo');
    const notas = formData.get('notas');
    const dateValue = formData.get('fecha');

    // FIX TIMEZONE: Force Noon UTC-Local hybrid to ensure correct day
    const fecha = new Date(dateValue + 'T12:00:00');

    console.log('💾 Guardando cambios:', { nombre, tipo, fecha, notas });

    await CaseService.updateAction(caseId, action.id, {
      name: nombre,
      createdTime: fecha,
      category: [tipo],
      description: notas,
      level: 2, // Force level 2 as it is now "reviewed"
      manualClassification: {
        tipo,
        fecha,
        notas,
        clasificadoPor: 'manual',
        timestamp: new Date()
      }
    });

    // Sync Timeline Immediately
    await syncTimelineLocal(caseId);

    // Close and Refresh
    document.getElementById('historial-edit-modal').remove();
    await runRapidTriage(caseId); // Refresh list

    alert('✅ Documento actualizado con éxito.');

  } catch (error) {
    console.error('Error guardando:', error);
    alert('Error al guardar: ' + error.message);
    btn.innerText = originalText;
    btn.disabled = false;
  }
}

async function handleAIAutocomplete(btn, action, modal, forcedToken = null) {
  const originalText = btn.innerHTML;
  btn.innerHTML = '⏳ Analizando...';
  btn.disabled = true;

  try {
    const { analyzeScannedImageWithGemini } = await import('./geminiAnalyzer.js');

    const apiKey = await AuthService.getGeminiApiKey();
    // Prioritize forcedToken (from Re-Auth), then State, then Storage
    let token = forcedToken || window.state?.googleToken || AuthService.getStoredGoogleToken();

    // PRE-CHECK: If no token, force Re-Auth immediately without failing fetch first
    if (!token) {
      console.warn("⚠️ No token found for Autocomplete. Triggering Re-Auth.");
      throw new Error("401 Auth Required"); // Throw artificial error to catch below
    }

    const analysis = await analyzeScannedImageWithGemini(
      action.driveFileLink,
      action.name || 'Sin título',
      apiKey,
      token
    );

    if (analysis.success && analysis.analysis) {
      const result = analysis.analysis;
      // Simple mapping
      const types = ['resolucion', 'sentencia', 'demanda', 'escrito', 'notificacion', 'prueba', 'contrato'];
      let foundType = 'otro';
      const aiType = (result.tipo_documento || '').toLowerCase();

      types.forEach(t => { if (aiType.includes(t)) foundType = t; });

      modal.querySelector('#input-tipo').value = foundType;
      if (result.fecha_documento) modal.querySelector('#input-fecha').value = result.fecha_documento;
      if (result.resumen) modal.querySelector('#input-notas').value = result.resumen;

      // Smart name suggestion
      if (action.name.match(/^(imagen|foto|scan|escaneo|documento|doc|sin.?titulo)/i)) {
        modal.querySelector('#input-nombre').value = `${foundType.toUpperCase()} - ${(result.resumen || '').substring(0, 30)}...`;
      }

      btn.innerHTML = '✅ Completado';
    } else {
      throw new Error(analysis.error || 'No se pudieron extraer datos.');
    }
  } catch (e) {
    console.warn("AI Analysis Error:", e);

    // UNIVERSAL ERROR HANDLING FOR PERMISSIONS
    const isPermissionError = e.message.includes('fetch') ||
      e.message.includes('CORS') ||
      e.message.includes('403') ||
      e.message.includes('401') ||
      e.message.includes('Auth Required') ||
      e.message.includes('Acceso denegado') ||
      e.message.includes('token expiró');

    if (isPermissionError) {
      // Create a custom modal to force a user gesture for re-authentication
      const reAuthModal = document.createElement('div');
      reAuthModal.style.position = 'fixed';
      reAuthModal.style.top = '0';
      reAuthModal.style.left = '0';
      reAuthModal.style.width = '100%';
      reAuthModal.style.height = '100%';
      reAuthModal.style.background = 'rgba(0,0,0,0.8)';
      reAuthModal.style.zIndex = '9999';
      reAuthModal.style.display = 'flex';
      reAuthModal.style.alignItems = 'center';
      reAuthModal.style.justifyContent = 'center';
      reAuthModal.innerHTML = `
        <div class="glass-container" style="padding: 2rem; max-width: 400px; text-align: center;">
            <h3 style="color: #facc15; margin-bottom: 1rem;">⚠️ Problema de Acceso</h3>
            <p style="color: white; margin-bottom: 1.5rem;">Google requiere renovar tus permisos para acceder al archivo.</p>
            <button id="btn-reauth" class="btn-gold" style="width: 100%; padding: 1rem;">
                🔐 RE-CONECTAR CUENTA GOOGLE
            </button>
            <button id="btn-cancel-reauth" class="btn-outline" style="width: 100%; margin-top: 1rem;">Cancelar</button>
        </div>
      `;
      document.body.appendChild(reAuthModal);

      // Handle Re-Auth Click (TRUSTED USER GESTURE)
      document.getElementById('btn-reauth').onclick = async () => {
        try {
          reAuthModal.remove();
          await AuthService.loginWithGoogle();
          const newToken = localStorage.getItem('google_access_token');
          console.log("🔄 Re-Auth Complete. New Token:", newToken ? newToken.substring(0, 10) + '...' : 'NULL');

          if (newToken && window.state) {
            console.log("⚡ Updating window.state.googleToken...");
            window.state.googleToken = newToken;
          }

          // Retry analysis with FORCED TOKEN
          console.log("🔁 Retrying analysis with FORCED TOKEN...");
          handleAIAutocomplete(btn, action, modal, newToken);
        } catch (loginErr) {
          alert("❌ Error: " + loginErr.message);
        }
      };

      document.getElementById('btn-cancel-reauth').onclick = () => {
        reAuthModal.remove();
        btn.innerHTML = '⚠️ Cancelado';
      };
    } else {
      alert('⚠️ Error en análisis: ' + e.message);
      btn.innerHTML = '⚠️ Error';
    }
  } finally {
    setTimeout(() => {
      btn.innerHTML = originalText;
      btn.disabled = false;
    }, 2000);
  }
}

async function syncTimelineLocal(caseId) {
  try {
    const { TimelineService } = await import('./timelineService.js');
    const actions = await CaseService.getActions(caseId);
    const existingEvents = await TimelineService.getEvents(caseId);

    for (const action of actions) {
      // Upsert Logic based on document ID linkage
      const matchingEvent = existingEvents.find(e =>
        e.documents && e.documents.some(d => d.id === action.id)
      );

      const eventData = {
        title: action.name || 'Documento sin título',
        description: action.description || '',
        date: action.createdTime || action.timestamp || new Date(),
        type: (action.category && action.category[0]) || 'document',
        documents: [{
          id: action.id || '',
          name: action.name || 'Sin nombre',
          link: action.driveFileLink || ''
        }]
      };

      if (matchingEvent) {
        // Update if changed
        if (matchingEvent.date.seconds !== eventData.date.seconds || matchingEvent.title !== eventData.title) {
          await TimelineService.updateEvent(caseId, matchingEvent.id, eventData);
        }
      } else {
        await TimelineService.createEvent(caseId, eventData);
      }
    }
  } catch (e) {
    console.error("Timeline Sync Error:", e);
  }
}

// =========================================================================
// 4. RENDERIZADO (SIMPLE HTML STRING BUILDER)
// =========================================================================

export async function analyzeDocumentsInHistorial(caseId) {
  return runRapidTriage(caseId);
}

export async function runRapidTriage(caseId) {
  const container = document.getElementById('actions-list');
  if (!container) return [];

  container.innerHTML = '<div class="spinner-small"></div> Cargando historial...';

  try {
    const actions = await CaseService.getActions(caseId);

    // Triage Logic
    const filesForTriage = actions.map(a => ({
      ...a,
      name: a.name || a.title || a.driveFileName || 'Documento sin título'
    }));

    const triageResult = processBatchWithTriage(filesForTriage);

    // Render Stats
    const statsHTML = generateTriageReport(triageResult).replace(/\n/g, '<br>');

    // Render List
    container.innerHTML = triageResult.all.map(doc => renderDocumentCard(doc, caseId)).join('');

    // Ensure logic controls are present
    ensureControls(caseId);

    return triageResult.all; // RETURN DOCS FOR DEEP ANALYSIS

  } catch (error) {
    container.innerHTML = `<p class="error-msg">Error: ${error.message}</p>`;
    return [];
  }
}

export async function runDeepAIAnalysis(caseId, forcedToken = null) {
  const btn = document.getElementById('btn-deep-analysis');
  if (btn) btn.innerHTML = '⏳ Preparando documentos...';

  try {
    // 1. Get Docs
    const docsToAnalyze = await analyzeDocumentsInHistorial(caseId);

    if (docsToAnalyze.length === 0) {
      alert('No hay documentos conectados a Drive para analizar.');
      return;
    }

    const apiKey = await AuthService.getGeminiApiKey();
    // Use forcedToken if provided (from Re-Auth), otherwise check state/storage
    const token = forcedToken || window.state?.googleToken || AuthService.getStoredGoogleToken();

    const updateProgress = (completed, total) => {
      if (btn) btn.innerHTML = `🤖 Analizando (${Math.round(completed / total * 100)}%)...`;
    };

    // Ensure token is passed
    if (!token) console.warn("⚠️ Warning: No Google Token found for batch analysis!");

    const results = await analyzeBatchWithGemini(docsToAnalyze, apiKey, token, updateProgress);

    // CHECK FOR AUTH FAILURES IN BATCH
    const authError = results.find(r => r.error && (r.error.includes('401') || r.error.includes('403') || r.error.includes('Permiso denegado')));

    if (authError) {
      console.warn("⚠️ Batch Analysis stopped due to Auth Error:", authError.error);
      if (btn) btn.innerHTML = '⚠️ Error de Acceso';

      // Trigger Custom Re-Auth Modal
      const reAuthModal = document.createElement('div');
      reAuthModal.style.position = 'fixed';
      reAuthModal.style.top = '0';
      reAuthModal.style.left = '0';
      reAuthModal.style.width = '100%';
      reAuthModal.style.height = '100%';
      reAuthModal.style.background = 'rgba(0,0,0,0.8)';
      reAuthModal.style.zIndex = '9999';
      reAuthModal.style.display = 'flex';
      reAuthModal.style.alignItems = 'center';
      reAuthModal.style.justifyContent = 'center';
      reAuthModal.innerHTML = `
            <div class="glass-container" style="padding: 2rem; max-width: 400px; text-align: center;">
                <h3 style="color: #facc15; margin-bottom: 1rem;">⚠️ Sesión Caducada</h3>
                <p style="color: white; margin-bottom: 1.5rem;">Google requiere renovar tus permisos para analizar estos documentos.</p>
                <button id="btn-reauth-batch" class="btn-gold" style="width: 100%; padding: 1rem;">
                    🔐 RE-CONECTAR AHORA
                </button>
                <button id="btn-cancel-batch" class="btn-outline" style="width: 100%; margin-top: 1rem;">Cancelar</button>
            </div>
        `;
      document.body.appendChild(reAuthModal);

      document.getElementById('btn-reauth-batch').onclick = async () => {
        try {
          reAuthModal.remove();
          await AuthService.loginWithGoogle();
          const newToken = localStorage.getItem('google_access_token');
          if (newToken && window.state) window.state.googleToken = newToken;

          // Retry Batch Analysis
          alert("✅ Permisos renovados. Reiniciando análisis masivo...");
          runDeepAIAnalysis(caseId, newToken); // Pass token explicitly
        } catch (loginErr) {
          alert("❌ Error: " + loginErr.message);
          if (btn) btn.disabled = false;
        }
      };

      document.getElementById('btn-cancel-batch').onclick = () => {
        reAuthModal.remove();
        if (btn) btn.disabled = false;
      };
      return; // Stop processing this batch
    }

    // Guardar resultados only if no auth error
    let successCount = 0;
    for (const res of results) {
      if (res.analysis) {
        const action = res.document;
        await CaseService.updateAction(caseId, action.id, {
          aiAnalysis: res.analysis,
          category: [res.analysis.tipo_documento || 'document'],
          description: res.analysis.resumen || action.description,
          level: res.analysis.requiere_atencion ? 3 : 1
        });
        successCount++;
      }
    }

    // Sincronizar Timeline
    await syncTimelineLocal(caseId);

    // UI Refresh
    await runRapidTriage(caseId);

    alert(`✅ Análisis Completado.\n\nDocumentos procesados: ${successCount}/${docsToAnalyze.length}\nTimeline actualizado.`);

  } catch (err) {
    console.error("Deep Analysis Error:", err);
    alert('Error en análisis masivo: ' + err.message);
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = originalText; }
  }
}


function renderDocumentCard(action, caseId) {
  const analysis = action.aiAnalysis || null;
  const manual = action.manualClassification || null;
  const triageLevel = action.triageLevel || (manual ? 2 : 3);

  let badges = '';
  if (action.isDuplicate) badges += `<span class="badge" style="background: #dc2626; color: white;">🗑️ DUPLICADO</span>`;
  else if (manual) badges += `<span class="badge" style="background: #10b981; color: white;">✅ MANUAL</span>`;
  else if (analysis) badges += `<span class="badge" style="background: #8b5cf6; color: white;">✨ IA</span>`;
  else {
    const icon = getTriageIcon(triageLevel);
    const color = triageLevel === 1 ? '#10b981' : triageLevel === 2 ? '#facc15' : '#ef4444';
    badges += `<span class="badge" style="background: ${color}; color: black;">${icon} NIVEL ${triageLevel}</span>`;
  }

  return `
      <div class="document-card level-${triageLevel}" style="opacity: ${action.isDuplicate ? 0.5 : 1}; position: relative;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; color: white; font-size: 1rem;">${action.name}</h3>
            ${action.driveFileLink ? `<a href="${action.driveFileLink}" target="_blank" class="btn-outline" style="font-size: 0.7rem;">📄 Ver</a>` : ''}
        </div>
        
        <div style="display: flex; gap: 1rem; color: #94a3b8; font-size: 0.8rem; margin: 0.5rem 0;">
            <span title="Fecha detectada o manual del documento">📅 Doc: ${safeDate(action.createdTime).toLocaleDateString()}</span>
            ${action.uploadTime ? `<span title="Fecha de carga al sistema" style="opacity: 0.7;">☁️ Subido: ${safeDate(action.uploadTime).toLocaleDateString()}</span>` : ''}
        </div>

        <div style="margin: 0.5rem 0;">${badges}</div>

        <!-- BOTÓN DE EDICIÓN GLOBAL CON DATA ATTRIBUTES -->
        <div style="margin-top: 1rem; text-align: right;">
            <button 
                class="btn-outline" 
                data-action="edit-document" 
                data-id="${action.id}" 
                data-case-id="${caseId}"
                style="border-color: #64748b; color: #cbd5e1; font-size: 0.8rem;">
                ✏️ Editar
            </button>
        </div>
      </div>
    `;
}

function ensureControls(caseId) {
  const container = document.getElementById('actions-list');
  if (!container || document.getElementById('analysis-controls-panel')) return;

  const panel = document.createElement('div');
  panel.id = 'analysis-controls-panel';
  panel.className = 'glass-container';
  panel.style.marginBottom = '1rem';
  panel.style.padding = '1rem';
  panel.innerHTML = `
        <div style="display: flex; gap: 10px; align-items: center; justify-content: space-between;">
            <span style="color: #94a3b8; font-size: 0.8rem;">MOTOR DE ANÁLISIS V3.0 (ACTIVO)</span>
            <div style="display: flex; gap: 5px;">
                <button id="btn-clean-duplicates" class="btn-outline" style="font-size: 0.8rem; border-color: #ef4444; color: #ef4444;">🗑️ Limpiar Duplicados</button>
                <button id="btn-refresh-list" class="btn-outline" style="font-size: 0.8rem;">🔄 Refrescar</button>
            </div>
        </div>
    `;

  container.parentElement.insertBefore(panel, container);

  document.getElementById('btn-refresh-list').onclick = () => runRapidTriage(caseId);
  document.getElementById('btn-clean-duplicates').onclick = () => cleanDuplicates(caseId);
}

async function cleanDuplicates(caseId) {
  if (!confirm('¿Estás seguro de eliminar permanentemente los archivos duplicados? Esta acción no se puede deshacer.')) return;

  try {
    const actions = await CaseService.getActions(caseId);
    const filesForTriage = actions.map(a => ({
      ...a,
      name: a.name || a.title || a.driveFileName || 'Documento sin título'
    }));

    const triageResult = processBatchWithTriage(filesForTriage);
    const duplicates = triageResult.duplicates.exact;

    if (duplicates.length === 0) {
      alert('No se encontraron duplicados exactos.');
      return;
    }

    let deleted = 0;
    for (const dup of duplicates) {
      console.log('Eliminando duplicado:', dup.file.name);
      await CaseService.deleteAction(caseId, dup.file.id);
      deleted++;
    }

    await runRapidTriage(caseId);
    alert(`✅ Se eliminaron ${deleted} documentos duplicados.`);

  } catch (error) {
    console.error("Error eliminando duplicados:", error);
    alert('Error: ' + error.message);
  }
}

// Global exposure for legacy calls (if any remaning in HTML)
window.analyzeDocumentsInHistorial = analyzeDocumentsInHistorial;
// window.reclassifyDocument NO LONGER NEEDED globally due to delegation
